import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Divider, Modal, notification } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Identifiable, RecordFieldSchema, RecordModel } from '../types/core';
import { ancanaApi } from '../api/ancanaApi';

interface ExportCsvModalProps<T> {
  recordModel: RecordModel<T>;
  recordFields: { [field: string]: RecordFieldSchema } | undefined;
}

function ExportCsvModal<T extends Identifiable>({ recordModel, recordFields }: ExportCsvModalProps<T>) {
  const [visible, setVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const allColumns = Object.entries(recordFields || {})
    .filter(([, v]: any) => !v.write_only)
    .map(([k]) => k);

  useEffect(() => {
    setSelectedColumns(allColumns);
  }, [recordFields]);

  const allChecked = selectedColumns.length === allColumns.length;
  const indeterminate = selectedColumns.length > 0 && selectedColumns.length < allColumns.length;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const columnsParam = selectedColumns.length < allColumns.length
        ? `?columns=${selectedColumns.join(',')}`
        : '';
      const res = await ancanaApi.get(`${recordModel.path}/export-csv/${columnsParam}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${recordModel.path}-export.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setVisible(false);
    } catch (e: any) {
      notification.error({ message: 'Export failed', description: e.message, placement: 'bottomRight' });
    } finally {
      setIsExporting(false);
    }
  };

  const toggleColumn = (fieldName: string, checked: boolean) => {
    setSelectedColumns(prev =>
      checked ? [...prev, fieldName] : prev.filter(c => c !== fieldName)
    );
  };

  return (
    <>
      <Button icon={<DownloadOutlined />} onClick={() => setVisible(true)}>
        Export CSV
      </Button>
      <Modal
        title="Export CSV"
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setVisible(false)}>Cancel</Button>,
          <Button
            key="export"
            type="primary"
            icon={<DownloadOutlined />}
            loading={isExporting}
            disabled={selectedColumns.length === 0}
            onClick={handleExport}
          >
            Export
          </Button>,
        ]}
        width={420}
      >
        <Checkbox
          indeterminate={indeterminate}
          checked={allChecked}
          onChange={e => setSelectedColumns(e.target.checked ? allColumns : [])}
        >
          Select all
        </Checkbox>
        <Divider style={{ margin: '8px 0' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto' }}>
          {Object.entries(recordFields || {})
            .filter(([, v]: any) => !v.write_only)
            .map(([fieldName, fieldSchema]) => (
              <Checkbox
                key={fieldName}
                checked={selectedColumns.includes(fieldName)}
                onChange={e => toggleColumn(fieldName, e.target.checked)}
              >
                {fieldSchema.label || fieldName}
              </Checkbox>
            ))}
        </div>
      </Modal>
    </>
  );
}

export default ExportCsvModal;
